import { useEffect, useRef, useState } from "react";
import { APIProvider, useMapsLibrary } from "@vis.gl/react-google-maps";
import {
  Controller,
  Control,
  FieldValues,
  Path,
  RegisterOptions,
  FieldPathValue,
} from "react-hook-form";

const { REACT_APP_GOOGLE_API_KEY: GOOGLE_API_KEY } = process.env;

interface AddressDetails {
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

interface AddressInputProps<FieldsType extends FieldValues> {
  name: Path<FieldsType>;
  control: Control<FieldsType>;
  rules?: RegisterOptions<FieldsType>;
  label?: string;
  required?: boolean;
  shouldUnregister?: boolean;
  placeholder?: string;
  requiredMessage?: string;
  defaultValue?: FieldPathValue<FieldsType, Path<FieldsType>>;
  onAddressSelect: (address: AddressDetails) => void;
}

const AddressInput = <FieldsType extends FieldValues>({
  name,
  control,
  rules,
  label,
  required,
  shouldUnregister,
  placeholder,
  requiredMessage,
  defaultValue,
  onAddressSelect,
}: AddressInputProps<FieldsType>) => {
  return (
    <Controller
      name={name}
      control={control}
      shouldUnregister={shouldUnregister}
      rules={{
        required: requiredMessage || (required && "Required"),
        ...(rules || {}),
      }}
      defaultValue={defaultValue}
      render={({ field, fieldState: { error } }) => (
        <div className="relative">
          {label && (
            <label className={`inline-block text-gray-500 pb-1 text-sm`}>
              {label}
              {(required || requiredMessage) && (
                <span className="text-red-600 ml-0.5 text-base">*</span>
              )}
            </label>
          )}
          <APIProvider apiKey={GOOGLE_API_KEY} language="en">
            <AddressInputField
              field={field}
              placeholder={placeholder || label}
              onPlaceSelect={(place) => {
                const address = extractAddress(place);
                onAddressSelect(address);
                field.onChange(address.address);
              }}
            />
          </APIProvider>
          <p className="text-red-600 font-normal text-sm m-0 p-0 ml-1 bottom-0 left-0 right-0">
            {error?.message}
          </p>
        </div>
      )}
    />
  );
};

interface AddressInputFieldProps {
  field: any;
  placeholder?: string;
  onPlaceSelect: (place: google.maps.places.PlaceResult) => void;
}

const AddressInputField: React.FC<AddressInputFieldProps> = ({
  field,
  placeholder,
  onPlaceSelect,
}) => {
  const [placeAutocomplete, setPlaceAutocomplete] =
    useState<google.maps.places.Autocomplete | null>(null);
  const inputReference = useRef<HTMLInputElement>(null);
  const places = useMapsLibrary("places");

  useEffect(() => {
    if (!places || !inputReference.current || placeAutocomplete) {
      return;
    }

    const autocomplete = new places.Autocomplete(inputReference.current, {
      fields: ["address_components"],
      componentRestrictions: { country: ["us"] },
      types: ["address"],
    });
    setPlaceAutocomplete(autocomplete);
  }, [placeAutocomplete, places]);

  useEffect(() => {
    if (!placeAutocomplete) {
      return;
    }
    const listener = placeAutocomplete.addListener("place_changed", () => {
      onPlaceSelect(placeAutocomplete.getPlace());
    });

    return () => {
      listener.remove();
    };
  }, [placeAutocomplete, onPlaceSelect]);

  return (
    <input
      id={field.name}
      {...field}
      ref={(ref) => {
        inputReference.current = ref;
        field.ref(ref);
      }}
      placeholder={placeholder}
      className="w-full appearance-none border border-gray-300 px-3 py-2.5 rounded-lg disabled:bg-gray-50 disabled:border-gray-200 text-gray-900 placeholder-gray-500 invalid:border-1 invalid:border-red-500 focus:outline-none focus:shadow-none focus:ring-0 focus:border-gray-900 text-sm bg-gray-50"
    />
  );
};

const extractAddress = (
  place: google.maps.places.PlaceResult
): AddressDetails => {
  const addressDetails: AddressDetails = {
    address: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",
  };
  const addressMapping = {
    street_number: (component) => {
      addressDetails.address = `${component.long_name} ${addressDetails.address}`;
    },
    route: (component) => {
      addressDetails.address += component.short_name;
    },
    postal_code: (component) => {
      addressDetails.zipCode = `${component.long_name}${addressDetails.zipCode}`;
    },
    postal_code_suffix: (component) => {
      addressDetails.zipCode = `${addressDetails.zipCode}-${component.long_name}`;
    },
    locality: (component) => {
      addressDetails.city = component.long_name;
    },
    administrative_area_level_1: (component) => {
      addressDetails.state = component.short_name;
    },
    country: (component) => {
      addressDetails.country = component.long_name;
    },
  };

  for (const component of place.address_components || []) {
    const componentType = component.types[0];
    if (addressMapping[componentType]) {
      addressMapping[componentType](component);
    }
  }
  return addressDetails;
};

export default AddressInput;
